import React, { useState } from 'react';
import { View } from '../../common';

interface ShareOptionsProps {
  whatsAppLink: string;
}
export const ShareOptions: React.FC<ShareOptionsProps> = () => {
  const [shareStatus, setShareStatus] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if Web Share API is supported
  const isWebShareSupported = navigator.share !== undefined;

  // Basic sharing function
  const handleNativeShare = async (data: ShareData) => {
    if (!isWebShareSupported) {
      setShareStatus('Web Share API not supported on this device');
      return;
    }

    try {
      const shareReponse = await navigator.share(data);
      setShareStatus('Content shared successfully!');
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setShareStatus('Share cancelled');
      } else {
        setShareStatus(`Error sharing: ${error.message}`);
      }
    }
  };

  // Share simple text/URL
  const shareBasicContent = () => {
    const shareOptions: ShareData = {
      title: 'ProductCatalogue',
      url: 'https://bkacontent.com/wp-content/uploads/2016/06/Depositphotos_31146757_l-2015.webp',
    };
    handleNativeShare(shareOptions);
  };

  // Fetch and share PDF
  const fetchAndSharePDF = async (pdfApiUrl: any) => {
    setIsLoading(true);
    setShareStatus('');

    try {
      // Fetch the PDF from API
      const response = await fetch(pdfApiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const file = new File([blob], 'document.pdf', {
        type: 'application/pdf',
      });

      // Check if the browser supports sharing files
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Shared PDF Document',
          text: 'Here is the PDF document you requested',
          files: [file],
        });
        setShareStatus('PDF shared successfully!');
      } else {
        // Fallback: Create a temporary URL and share the link
        const tempUrl = URL.createObjectURL(blob);

        // For WhatsApp specifically, we need to use their URL scheme
        shareViaWhatsApp(tempUrl, 'Check out this PDF document');

        // Clean up the temporary URL after a delay
        setTimeout(() => URL.revokeObjectURL(tempUrl), 60000);
      }
    } catch (error) {
      setShareStatus(`Error fetching PDF: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Share via WhatsApp specifically
  const shareViaWhatsApp = (url: string, message: string) => {
    const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(message + ' ' + url)}`;
    const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(
      message + ' ' + url
    )}`;

    // Try to open WhatsApp app first, fallback to web version
    try {
      window.open(whatsappUrl, '_blank');
      setShareStatus('Opening WhatsApp...');
    } catch (error) {
      window.open(webWhatsappUrl, '_blank');
      setShareStatus('Opening WhatsApp Web...');
    }
  };

  // Share PDF directly via WhatsApp
  const sharePDFViaWhatsApp = () => {
    if (!pdfUrl) {
      setShareStatus('Please enter a PDF URL');
      return;
    }
    fetchAndSharePDF(pdfUrl);
  };

  // Fallback sharing options when Web Share API isn't available
  const showFallbackOptions = (data: any) => {
    const shareText = `${data.title}\n${data.text}\n${data.url}`;

    // Copy to clipboard
    navigator.clipboard
      .writeText(shareText)
      .then(() => {
        setShareStatus('Link copied to clipboard!');
      })
      .catch(() => {
        setShareStatus('Could not copy to clipboard');
      });
  };

  return <View className="share-options"></View>;
};
